<template>
  <nav class="breadcrumbs" data-test="search-breadcrumbs">
    <search-breadcrumbs-generator
      v-if="breadcrumbsInternal2"
      :breadcrumbs="breadcrumbsInternal2"
      :with-counts="withCounts"
      @update-rendered-url="({ label, url }) => updateRenderedUrl(label, url)"
    >
      <pu-link
        v-bind="homeBreadcrumb"
        exclude-type="fullMinusType"
        :non-seo="true"
      >
        <translate>Home</translate>
      </pu-link>
      <span class="crumb">&rsaquo;</span>
    </search-breadcrumbs-generator>
    <nuxt-to-meta v-if="showSchema" :nuxt2meta="head" />
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { Breadcrumb } from '~/apps/search/schemas'
import type { BreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import { useBreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import type { SearchFilters } from '~/types/search'
import { getFacetRules } from '~/utility/facets'
import { CATEGORIES } from '../../apps/pu-links/simpleCategories'
import { HomepageLinkGenerator } from '../../apps/url/generators/HomepageLinkGenerator'
import { isGlamping } from '../../utility/glamping'
import { computeBreadcrumbs } from '~/apps/breadcrumbs/computeBreadcrumbs'

const props = defineProps<{
  breadcrumbs: Breadcrumb[]
  showSchema?: boolean
  counts?: Record<string, number>
  withCounts?: boolean
  linkAll?: boolean
}>()

const searchStore = useSearchStoreSetup(usePinia())
const route = useRoute()
const langCode = (route.params.lang as string) || 'en-gb'

const isGlampingCategory = computed(() => {
  return isGlamping(searchStore.searchFilters.categoryIds || [])
})

const isSeo = computed(() => {
  const searchFilters = searchStore.searchFilters
  // FIXED: Properly compare glamping categories using existing isGlamping helper
  const isGlampingCategories = isGlamping(searchFilters.categoryIds || [])

  // Non-SEO if has dates (arrival/departure)
  if (searchFilters.dates?.arrive || searchFilters.dates?.depart) {
    return false
  }

  // FIXED: Non-SEO only if has NON-DEFAULT party size params
  // Default party params (adults: 2, childAges: []) should not affect SEO status
  const hasNonDefaultPartyParams =
    searchFilters.party &&
    ((searchFilters.party.adults !== undefined &&
      searchFilters.party.adults !== 2) ||
      (searchFilters.party.childAges !== undefined &&
        searchFilters.party.childAges.length > 0))

  if (hasNonDefaultPartyParams) {
    return false
  }

  // Non-SEO if has more than 1 facet
  if (searchFilters.facets?.length && searchFilters.facets.length > 1) {
    return false
  }

  // Non-SEO if has multiple categories (unless it's the glamping combination)
  if ((searchFilters.categoryIds?.length ?? 0) > 1 && !isGlampingCategories) {
    return false
  }

  return true
})

const baseLangPrefix = computed(() => {
  return langCode === 'en-gb' ? '/' : `/${langCode}/`
})

function getBaseUrl(useSearch: boolean) {
  if (!isSeo.value || useSearch) {
    return `${baseLangPrefix.value}search`
  }
  const categoryIds = searchStore.searchFilters.categoryIds
  if (categoryIds?.length === 1) {
    const rootSlug = CATEGORIES[categoryIds[0]]
    return `${baseLangPrefix.value}${rootSlug}`
  }
  if (isGlampingCategory.value) {
    return `${baseLangPrefix.value}glamping`
  }
  return `${baseLangPrefix.value}campsites`
}

const searchFilters = computed((): SearchFilters => {
  const filters = searchStore.searchFilters
  return {
    ...filters,
    hierarchyPath: filters.hierarchyPath ?? undefined,
  }
})

const breadcrumbCountsSearchFilters = computed(() => {
  return {
    ...searchFilters.value,
    facets: [],
  }
})

const isDatedSearch = computed(() => {
  return Boolean(searchFilters.value?.dates?.arrive)
})

const { breadcrumbCounts } = useBreadcrumbCounts(
  breadcrumbCountsSearchFilters,
) as { breadcrumbCounts: Ref<BreadcrumbCounts> }

// Faceted breadcrumb counts (WITH facets included)
// Used to determine if breadcrumb should be linkable - don't link to zero-result pages
const facetedBreadcrumbCountsSearchFilters = computed(() => {
  return { ...searchFilters.value } // Keep facets
})

const { breadcrumbCounts: facetedBreadcrumbCounts } = useBreadcrumbCounts(
  facetedBreadcrumbCountsSearchFilters,
) as { breadcrumbCounts: Ref<BreadcrumbCounts> }

const { data: facetRules } = await useAsyncData(
  `search-breadcrumb-facet-rules-${JSON.stringify(searchFilters.value)}`,
  async () => {
    if (!searchFilters.value.facets?.length) {
      return []
    }
    const rules = await Promise.all(
      searchFilters.value.facets.map((facet) => getFacetRules(facet, langCode)),
    )
    return rules.flat()
  },
  {
    watch: [searchFilters],
    server: true,
    lazy: import.meta.client,
  },
)

const hierarchyCounts = computed(
  () => breadcrumbCounts.value?.bookableFacetHierarchy ?? {},
)

const facetedHierarchyCounts = computed(
  () => facetedBreadcrumbCounts.value?.bookableFacetHierarchy ?? {},
)

const homeBreadcrumb = computed(() => {
  const homeLinkGenerator = new HomepageLinkGenerator()
  return homeLinkGenerator.getUrlData(searchFilters.value)
})

const breadcrumbsInternal2 = computed(() => {
  return computeBreadcrumbs({
    breadcrumbs: props.breadcrumbs,
    counts: props.counts,
    searchFilters: searchFilters.value,
    facetRules: facetRules.value ?? null,
    hierarchyCounts: hierarchyCounts.value,
    facetedHierarchyCounts: facetedHierarchyCounts.value,
    isGlampingCategory: isGlampingCategory.value,
    isDatedSearch: isDatedSearch.value,
    getBaseUrl,
  })
})

const renderedUrls = ref<Map<string, string>>(new Map())

const updateRenderedUrl = (label: string, url: string) => {
  renderedUrls.value.set(label, url)
}

const head = computed(() => {
  if (props.showSchema) {
    const allBreadcrumbs = [
      {
        label: 'Home',
        id: `https://${useRuntimeConfig().public.envUrl}${homeBreadcrumb.value.path}`,
      },
      ...breadcrumbsInternal2.value
        .filter(
          (crumb) =>
            crumb.linkable &&
            renderedUrls.value.has(crumb.label) &&
            crumb.hierarchyPath,
        )
        .map((crumb) => ({
          label: crumb.label,
          id: `https://${useRuntimeConfig().public.envUrl}${renderedUrls.value.get(crumb.label)}`,
        })),
    ]
    // Add the last breadcrumb
    const lastBreadcrumb =
      breadcrumbsInternal2.value[breadcrumbsInternal2.value.length - 1]
    if (lastBreadcrumb && lastBreadcrumb.linkable) {
      let finalUrl: string

      // Use rendered URL from pu-link if available (client-side), otherwise compute it (SSR)
      if (renderedUrls.value.has(lastBreadcrumb.label)) {
        finalUrl = renderedUrls.value.get(lastBreadcrumb.label)!
      } else {
        // Fallback: Apply same pu-link logic for SSR
        finalUrl = lastBreadcrumb.url
        const facets = searchStore.searchFilters.facets || []

        if (facets.length > 0 && !lastBreadcrumb.removeFacetQuery) {
          if (lastBreadcrumb.nonSeo) {
            // Query param format: ?facet=...
            const separator = finalUrl.includes('?') ? '&' : '?'
            finalUrl = `${finalUrl}${separator}facet=${facets.join(',')}`
          } else {
            // SEO URL format: /-/facet/
            const facetPath = `/-/${facets[0]}/`
            if (!finalUrl.includes(facetPath)) {
              finalUrl = `${finalUrl.replace(/\/$/, '')}${facetPath}`
            }
          }
        }
      }

      allBreadcrumbs.push({
        label: lastBreadcrumb.label,
        id: `https://${useRuntimeConfig().public.envUrl}${finalUrl}`,
      })
    }
    return {
      script: [
        {
          hid: 'ldjson-schema-search-breadcrumbs',
          type: 'application/ld+json',
          body: true,
          json: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: allBreadcrumbs.map((breadcrumb, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@id': breadcrumb.id,
                name: breadcrumb.label,
              },
            })),
          },
        },
      ],
    }
  }
  return {}
})
</script>

<style>
@import url('~/assets/css/breadcrumbs.css');
</style>
